import React, { useState } from 'react';
import {
  Box, Tabs, Tab, Table, TableBody, TableCell, TableHead, TableRow, Chip,
  Typography, Link, Skeleton, ToggleButton, ToggleButtonGroup, Tooltip,
  Paper,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { OpenInNew, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { seoAPI } from '../../services/api';
import Panel from '../crt/Panel';

/**
 * ACTION CENTER — the "do today" panel.
 *
 * Three tabs surface the highest-leverage SEO actions hiding in GSC data:
 *
 *   • Easy wins:   queries ranking 5-20 with real impressions. Each one
 *                  is a "push to top 10" candidate — we already rank,
 *                  the page is relevant, just need on-page work.
 *   • CTR rewrites: queries with impressions but <2% CTR. Title/metadesc
 *                  isn't pulling clicks — rewrite for instant lift.
 *   • Movers:       biggest position gains/losses week-over-week. Catches
 *                  algo shifts + verifies recent optimizations.
 *
 * Backend already had this data via /api/v1/seo/keyword-opportunities;
 * it just wasn't surfaced in the UI. This is the actionable view the
 * dashboard was missing.
 */

const fmt = (n: number | null | undefined) =>
  n == null ? '—' : Number(n).toLocaleString();

const ActionCenter: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [site, setSite] = useState<string>('all');
  const [days, setDays] = useState<number>(28);

  const { data, isLoading } = useQuery({
    queryKey: ['seo-keyword-opps', site, days],
    queryFn: () => seoAPI.getKeywordOpportunities({
      site: site === 'all' ? undefined : site,
      days,
      limit: 25,
    }),
    staleTime: 5 * 60_000,
  });

  const near = data?.near_rank || [];
  const lowCtr = data?.low_ctr || [];
  const movers = data?.top_movers || [];

  return (
    <Panel label="ACTION CENTER :: WHAT TO DO TODAY">
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', flexGrow: 1 }}>
            Highest-leverage SEO work surfaced from GSC. Click a query to open it on Google;
            click a page to open it on the site.
          </Typography>
          <ToggleButtonGroup size="small" exclusive value={site} onChange={(_, v) => v && setSite(v)}>
            <ToggleButton value="all">all</ToggleButton>
            <ToggleButton value="spoutlore">SL</ToggleButton>
            <ToggleButton value="mallbrats">MB</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}>
            <ToggleButton value={7}>7d</ToggleButton>
            <ToggleButton value={28}>28d</ToggleButton>
            <ToggleButton value={90}>90d</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tab label={
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>🎯 Easy wins</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {near.length} queries ranking 5-20
              </Typography>
            </Box>
          } />
          <Tab label={
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>✏️ CTR rewrites</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {lowCtr.length} pages with traffic but low CTR
              </Typography>
            </Box>
          } />
          <Tab label={
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>📈 Movers</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {movers.length} biggest changes
              </Typography>
            </Box>
          } />
        </Tabs>

        {isLoading && <Skeleton variant="rectangular" height={300} />}

        {/* Easy wins */}
        {!isLoading && tab === 0 && (
          <>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Why this matters
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                These queries already rank you on Google — just not on page 1. A targeted
                blog post, expanded show notes, or improved meta description on the listed
                page is usually enough to push the query to top 5. Highest impression count = highest priority.
              </Typography>
            </Paper>
            <OpportunityTable rows={near} kind="near_rank" />
          </>
        )}

        {/* CTR rewrites */}
        {!isLoading && tab === 1 && (
          <>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Why this matters
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                These pages are visible in Google results but the title/meta isn't enticing
                enough — &lt;2% click-through despite real impressions. Rewriting the
                Yoast title/metadesc to be more specific or compelling typically doubles CTR
                and is fast SEO — no new content needed.
              </Typography>
            </Paper>
            <OpportunityTable rows={lowCtr} kind="low_ctr" />
          </>
        )}

        {/* Movers */}
        {!isLoading && tab === 2 && (
          <>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Why this matters
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                Biggest position changes for queries with traffic, comparing the latest
                {' '}{Math.floor(days / 2)} days vs the prior {Math.floor(days / 2)}.
                Up arrows = pages climbing (SEO work paying off, double down).
                Down arrows = pages dropping (investigate before they fall further).
              </Typography>
            </Paper>
            <OpportunityTable rows={movers} kind="movers" />
          </>
        )}
      </Box>
    </Panel>
  );
};

interface OpportunityRow {
  site_slug: string;
  query: string;
  page: string;
  impressions: number;
  clicks?: number;
  avg_position?: number;
  ctr?: number;
  recent_position?: number;
  prior_position?: number;
  position_change?: number;
}

const OpportunityTable: React.FC<{
  rows: OpportunityRow[];
  kind: 'near_rank' | 'low_ctr' | 'movers';
}> = ({ rows, kind }) => {
  if (rows.length === 0) {
    return (
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', py: 4, textAlign: 'center' }}>
        No data yet. The GSC daily sync needs ~14 days of data before this populates well.
      </Typography>
    );
  }
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Query</TableCell>
            <TableCell>Page</TableCell>
            <TableCell align="right">Impr.</TableCell>
            {kind === 'movers' ? (
              <>
                <TableCell align="right">Was</TableCell>
                <TableCell align="right">Now</TableCell>
                <TableCell align="right">Δ pos</TableCell>
              </>
            ) : (
              <>
                <TableCell align="right">CTR</TableCell>
                <TableCell align="right">Pos</TableCell>
              </>
            )}
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => {
            const path = (r.page || '').replace(/^https?:\/\/[^/]+/, '') || '/';
            const googleQueryUrl = `https://www.google.com/search?q=${encodeURIComponent(r.query)}`;
            return (
              <TableRow key={`${r.site_slug}-${r.query}-${r.page}-${i}`} hover>
                <TableCell>
                  <Link href={googleQueryUrl} target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit' }}>
                    {r.query}
                  </Link>
                  <Chip
                    label={r.site_slug}
                    size="small"
                    sx={{ ml: 1, height: 18, fontSize: 10 }}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 280 }}>
                  <Tooltip title={r.page}>
                    <Link href={r.page} target="_blank" rel="noopener noreferrer"
                          sx={{ color: 'inherit', fontFamily: 'monospace', fontSize: 11,
                                whiteSpace: 'nowrap', overflow: 'hidden',
                                textOverflow: 'ellipsis', display: 'block' }}>
                      {path} <OpenInNew fontSize="inherit" />
                    </Link>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">{fmt(r.impressions)}</TableCell>
                {kind === 'movers' ? (
                  <>
                    <TableCell align="right">{r.prior_position?.toFixed(1) ?? '—'}</TableCell>
                    <TableCell align="right">{r.recent_position?.toFixed(1) ?? '—'}</TableCell>
                    <TableCell align="right">
                      {r.position_change != null && (
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.25,
                          color: r.position_change > 0 ? 'success.main' : 'error.main',
                          fontWeight: 600,
                        }}>
                          {r.position_change > 0
                            ? <ArrowUpward fontSize="small" />
                            : <ArrowDownward fontSize="small" />}
                          {Math.abs(r.position_change).toFixed(1)}
                        </Box>
                      )}
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell align="right">
                      {r.ctr != null ? `${(r.ctr * 100).toFixed(1)}%` : '—'}
                    </TableCell>
                    <TableCell align="right">{r.avg_position?.toFixed(1) ?? '—'}</TableCell>
                  </>
                )}
                <TableCell sx={{ maxWidth: 240 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {kind === 'near_rank' &&
                      'Add transcript / expand show notes for this query'}
                    {kind === 'low_ctr' &&
                      'Rewrite Yoast title + metadesc for this page'}
                    {kind === 'movers' && r.position_change != null && (
                      r.position_change > 0
                        ? 'Climbing — promote on socials, double-down content'
                        : 'Dropping — check page status, refresh content'
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default ActionCenter;
